<template>
  <div class="main-container">
    <div>
      <!-- Header Section -->
      <v-row class="pa-2 header-row">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <v-btn icon class="back-button my-4 mx-4" @click="confirmBackNavigation">
            <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
          </v-btn>
          <!-- Conditionally hide the View Available Bins and Add Awaiting Spare buttons for Salesperson,
               and also hide them if the user lacks permission -->
          <div class="d-flex">
            <v-btn 
           
              color="#28a745" 
              class="view-bins-button mx-4" 
              dark 
              @click="openBinsDialog"
            >
              View Available Bins
            </v-btn>
            <v-btn 
           
              color="#AC1726" 
              class="add-spare-button mx-4" 
              dark 
              @click="navigateToAddSpare"
            >
              Add Awaiting Spare
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Loader while fetching data -->
      <v-row v-if="loading" justify="center" align="center" class="loader-row">
        <v-progress-circular indeterminate color="#AC1726"></v-progress-circular>
      </v-row>

      <!-- Awaiting Spares Section -->
      <div v-if="!loading" class="spares-container">
        <!-- Search and Filter -->
        <v-row justify="center" class="search-filter-row">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Awaiting Spares"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedStatus"
              :items="statusOptionsWithClear"
              placeholder="Filter by Status"
              filled
              dense
              rounded
              class="my-3"
              @change="handleSelectChange"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Awaiting Spares Cards -->
        <v-row>
          <v-col 
            cols="12" 
            sm="6" 
            md="4" 
            lg="3" 
            v-for="(spare, index) in paginatedSpares" 
            :key="spare.id || index"
          >
            <v-card class="modern-card" elevation="6" hover>
              <!-- Header with Status, Spare ID, Extra Info and Copy Buttons -->
              <v-row class="d-flex justify-end pr-3 pt-3 align-center">
                <!-- Status Chip -->
                <v-chip 
                  :color="getChipColor(spare.status)" 
                  dark 
                  label
                  class="status-chip ml-2"
                >
                  {{ spare.status || 'Unknown' }}
                </v-chip>
                <!-- Spare ID Chip -->
                <v-chip 
                  color="grey" 
                  dark 
                  label
                  class="spare-id-chip ml-2"
                >
                  ID: {{ spare.sparesId || 'Unknown' }}
                </v-chip>
                <!-- Extra Info Button -->
                <v-btn 
                  icon 
                  @click="openExtraInfo(spare)"
                  title="Extra Info"
                  class="ml-2"
                >
                  <v-icon color="#AC1726">mdi-information</v-icon>
                </v-btn>
              </v-row>
              
              <!-- Card Content -->
              <v-card-text class="pa-4">
                <!-- Created Date -->
                <v-row justify="end">
                  <v-col cols="12" class="py-2 text-right">
                    <span class="text-caption">Created Date:</span>
                    <span class="font-weight-medium">{{ formatDate(spare.createdDate) || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <!-- Spare Details -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Journal Number:</span>
                    <span class="font-weight-medium">{{ spare.journalNumber || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Bin Number:</span>
                    <span class="font-weight-medium">{{ spare.binNumber || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Code:</span>
                    <span class="font-weight-medium">{{ spare.code || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Quantity:</span>
                    <span class="font-weight-medium">{{ spare.qty || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Projected Date:</span>
                    <span class="font-weight-medium">{{ formatDate(spare.projectedDate) || 'N/A' }}</span>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Card Actions -->
              <!-- The Open Spare button is shown if the user is a Salesperson (view only)
                   or if they have edit permission -->
              <v-card-actions class="d-flex justify-between align-center">
                <v-btn 
                  rounded 
                  dark 
                  color="#AC1726" 
                  @click.stop="openOrEditSpare(spare)"
                  class="open-spare-button flex-grow-1 mr-2"
                >
                  {{ isSalesperson ? 'Open Spare' : 'Edit Spare' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Load More Button -->
        <v-row justify="center" v-if="canLoadMore">
          <v-btn 
            color="#AC1726" 
            @click="loadMore" 
            :disabled="!canLoadMore"
            class="load-more-button"
          >
            Load More
          </v-btn>
        </v-row>
      </div>
    </div>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#AC1726" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="#28a745" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Extra Info Dialog -->
    <v-dialog v-model="extraInfoDialog.visible" max-width="500" class="extra-info-dialog">
      <v-card>
        <v-card-title class="headline dialog-title">
          Extra Information
          <v-spacer></v-spacer>
          <v-btn icon @click.stop="copyCardContent(extraInfoSpare)" title="Copy Card Content">
            <v-icon color="#AC1726">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn icon @click="extraInfoDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Journal Number:</strong> {{ extraInfoSpare.journalNumber || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Code:</strong> {{ extraInfoSpare.code || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Quantity:</strong> {{ extraInfoSpare.qty || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Reason:</strong> {{ extraInfoSpare.reason || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Comment:</strong> {{ extraInfoSpare.comment || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>4 Week Projected Date:</strong> {{ formatDate(extraInfoSpare.projectedDate) || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Free of Charge:</strong> {{ extraInfoSpare.freeOfCharge || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bins Dialog Component (Full-Screen Dialog) -->
    <BinsDialog
      :visible.sync="binsDialog.visible"
      :available-bins="availableBinsList"
      :taken-bins="takenBinsList"
    />

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc 
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";
import BinsDialog from "@/components/BinsDialog.vue";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  name: "AllAwaitingSpares",
  components: {
    BinsDialog,
  },
  data() {
    return {
      spares: [],
      search: '',
      selectedStatus: '',
      statusOptions: [
        { text: "Factory Repair", value: "Factory Repair" },
        { text: "Local Repair", value: "Local Repair" },
        { text: "Completed", value: "Completed" },
      ],
      userRole: "",
      // Awaiting spares permissions fetched from Firestore (assumed under "awaitingSpares")
      userPermissions: {},
      salespersonDialog: false,
      selectedDelivery: {},
      loading: true,
      backDialog: false,
      visibleCount: 8,
      loadIncrement: 8,
      binsDialog: {
        visible: false,
      },
      boxes: Array.from({ length: 40 }, (_, i) => ({
        number: i + 1,
        taken: false,
        selected: false,
        unavailable: false,
      })),
      extraInfoDialog: {
        visible: false,
      },
      extraInfoSpare: {},
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },
  computed: {
    isSalesperson() {
      return this.userRole === 'Salesperson';
    },
    filteredSpares() {
      let filtered = this.spares;
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(spare =>
          (spare.sparesId && spare.sparesId.toString().toLowerCase().includes(searchLower)) ||
          (spare.status && spare.status.toLowerCase().includes(searchLower)) ||
          (spare.journalNumber && spare.journalNumber.toString().toLowerCase().includes(searchLower)) ||
          (spare.binNumber && spare.binNumber.toString().toLowerCase().includes(searchLower)) ||
          (spare.createdDate && this.formatDate(spare.createdDate).toLowerCase().includes(searchLower)) ||
          (spare.code && spare.code.toString().toLowerCase().includes(searchLower)) ||
          (spare.qty && spare.qty.toString().toLowerCase().includes(searchLower)) ||
          (spare.projectedDate && this.formatDate(spare.projectedDate).toLowerCase().includes(searchLower))
        );
      }
      if (this.selectedStatus && this.selectedStatus !== 'Clear filter') {
        filtered = filtered.filter(spare => spare.status === this.selectedStatus);
      }
      filtered = filtered.filter(spare => spare.status !== "Completed" && spare.status !== "Rejected");
      return filtered.sort((a, b) => parseInt(b.sparesId) - parseInt(a.sparesId));
    },
    statusOptionsWithClear() {
      return ['Clear filter', ...this.statusOptions];
    },
    paginatedSpares() {
      return this.filteredSpares.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.filteredSpares.length;
    },
    availableBinsList() {
      return this.boxes.filter(box => !box.taken && !box.unavailable);
    },
    takenBinsList() {
      return this.boxes.filter(box => box.taken);
    },
    /**
     * Returns true only if neither Add_Button nor Edit_Details is enabled.
     */
    hasAwaitingSparesEditPermission() {
      return !(this.userPermissions.Add_Button === true || this.userPermissions.Edit_Details === true);
    },
  },
  watch: {
    search() {
      this.resetPagination();
    },
    selectedStatus() {
      this.resetPagination();
    },
  },
  async created() {
    this.loading = true;
    await this.fetchUserRole();
    await this.fetchSpares();
    await this.initializeBoxes();
    this.loading = false;
  },
  methods: {
    async fetchUserRole() {
      try {
        await new Promise((resolve) => {
          onAuthStateChanged(auth, async (user) => {
            if (user) {
              const uid = user.uid;
              const userDoc = await getDoc(doc(db, "users", uid));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                this.userRole = userData.role || "";
                localStorage.setItem('userRole', this.userRole);
                // Awaiting spares permissions stored under "awaitingSpares"
                this.userPermissions = userData.awaitingSpares || {};
              } else {
                console.warn("User document not found.");
                this.userRole = "Guest";
              }
            } else {
              console.warn("No user is logged in.");
              this.userRole = "Guest";
            }
            resolve();
          });
        });
      } catch (error) {
        console.error("Error fetching user role:", error);
        this.snackbarMessage = "Failed to fetch user role.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    async fetchSpares() {
      try {
        const sparesCollection = collection(db, "AwaitingSpares");
        const sparesSnapshot = await getDocs(sparesCollection);
        const spares = [];
        sparesSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          spares.push({
            id: docSnap.id,
            sparesId: data.sparesId,
            status: data.status,
            journalNumber: data.journalNumber,
            binNumber: data.binNumber,
            createdDate: data.createdDate,
            code: data.code || 'N/A',
            qty: data.qty || 'N/A',
            projectedDate: data.projectedDate || 'N/A',
            imageUrl: data.imageUrl || '',
            reason: data.reason || 'N/A',
            comment: data.comment || 'N/A',
            freeOfCharge: data.freeOfCharge || 'N/A',
          });
        });
        this.spares = spares;
      } catch (error) {
        console.error("Error fetching spares:", error);
        this.snackbarMessage = "Failed to fetch spares.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    async initializeBoxes() {
      this.boxes.forEach(box => {
        box.taken = false;
        box.selected = false;
        box.unavailable = false;
      });
      const takenBins = this.spares
        .filter(spare => spare.binNumber && spare.status !== "Completed")
        .map(spare => spare.binNumber.toString());
      this.boxes.forEach(box => {
        if (takenBins.includes(box.number.toString())) {
          box.taken = true;
          box.unavailable = true;
        }
      });
    },
    handleSelectChange(value) {
      if (value === 'Clear filter') {
        this.clearFilters();
      }
    },
    confirmBackNavigation() {
      this.backDialog = true;
    },
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },
    handleCardClick(spare) {
      // Since the controls are hidden if not permitted, simply navigate.
      this.navigateTo(spare);
    },
    navigateTo(spare) {
      if (spare.id) {
        localStorage.setItem('selectedSpare', spare.id);
        this.$router.push({ path: '/EditSpares' });
      }
    },
    navigateToAddSpare() {
      // Directly navigate because the button is only rendered if permitted.
      this.$router.push({ path: '/AddSpares' });
    },
    clearFilters() {
      this.selectedStatus = '';
      this.search = '';
    },
    loadMore() {
      this.visibleCount += this.loadIncrement;
    },
    resetPagination() {
      this.visibleCount = this.loadIncrement;
    },
    openBinsDialog() {
      this.binsDialog.visible = true;
    },
    closeBinsDialog() {
      this.binsDialog.visible = false;
    },
    async downloadImageAsZip(spare) {
      if (spare.imageUrl) {
        try {
          this.loading = true;
          const response = await fetch(spare.imageUrl);
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const blob = await response.blob();
          const zip = new JSZip();
          const urlParts = spare.imageUrl.split('/');
          const filename = urlParts[urlParts.length - 1].split('?')[0];
          zip.file(filename, blob);
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          saveAs(zipBlob, `spare_${spare.sparesId}_image.zip`);
          this.showSnackbar("Image downloaded successfully.");
        } catch (error) {
          console.error("Error downloading image as ZIP:", error);
          this.showSnackbar("Failed to download image.");
        } finally {
          this.loading = false;
        }
      } else {
        this.showSnackbar("No images found.");
      }
    },
    openExtraInfo(spare) {
      this.extraInfoSpare = spare;
      this.extraInfoDialog.visible = true;
    },
    // Define the openOrEditSpare method
    openOrEditSpare(spare) {
      if (this.isSalesperson) {
        // For Salesperson, navigate to a view-only page or open a details dialog.
        // Here we choose to navigate to a view page.
        localStorage.setItem('selectedSpare', spare.id);
        this.$router.push({ path: '/ViewSpares' });
      } else {
        // For other users, navigate to the edit page.
        localStorage.setItem('selectedSpare', spare.id);
        this.$router.push({ path: '/EditSpares' });
      }
    },
    copyCardContent(spare) {
      const content = `Status: ${spare.status || 'Unknown'}
ID: ${spare.sparesId || 'Unknown'}
Created Date: ${this.formatDate(spare.createdDate) || 'Unknown'}
Journal Number: ${spare.journalNumber || 'Unknown'}
Bin Number: ${spare.binNumber || 'Unknown'}
Code: ${spare.code || 'N/A'}
Quantity: ${spare.qty || 'N/A'}
Projected Date: ${this.formatDate(spare.projectedDate) || 'N/A'}`;
      navigator.clipboard.writeText(content)
        .then(() => {
          this.showSnackbar("Card content copied successfully.");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          this.showSnackbar("Failed to copy card content.");
        });
    },
    showSnackbar(message) {
      this.snackbarMessage = message;
      this.snackbar = true;
    },
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'Unknown' || dateStr === 'N/A') return 'N/A';
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getChipColor(status) {
      if (!status) return 'grey';
      switch(status.toLowerCase()) {
        case 'factory repair':
          return 'light-blue';
        case 'local repair':
          return 'orange';
        case 'completed':
          return 'green';
        case 'pending credit':
          return 'orange';
        case 'booked':
          return 'teal';
        case 'inspection':
          return 'blue';
        case 'labels and packaging':
          return 'purple';
        case 'move to stores':
          return 'brown';
        case 'rejected':
          return 'red';
        default:
          return 'grey';
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

body, div, span, v-card-title, v-card-text, v-row, v-col, v-card, v-chip, v-avatar, v-toolbar-title {
  font-family: 'Montserrat', sans-serif;
}

.main-container {
  background-color: #121212; /* Dark background for contrast */
  min-height: 100vh;
}

.header-row {
  background-color: #1e1e1e; /* Slightly lighter than main background */
  border-bottom: 1px solid #333;
}

.spares-container {
  background-color: #ffffff; /* White background */
  border-radius: 20px 20px 0 0;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  min-height: 70%;
}

.search-filter-row {
  margin-bottom: 24px;
}

.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  margin: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* Urgency Square Styling */
.urgency-square {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.red-square {
  background-color: red;
}

.orange-square {
  background-color: orange;
}

.green-square {
  background-color: green;
}

.grey-square {
  background-color: grey;
}

/* Status Chip Styling */
.status-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

/* Delivery Number Chip Styling */
.delivery-number-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
}

/* Open Spare Button Styling */
.open-spare-button {
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.open-spare-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

/* Back Button Styling */
.back-button {
  color: white;
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
}

.back-button:hover {
  transform: scale(1.1);
}

.back-button .v-icon {
  color: white;
}

/* Add Spare & View Bins Button Styling */
.add-spare-button, .view-bins-button {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.add-spare-button:hover {
  background-color: #e63946;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.view-bins-button:hover {
  background-color: #218838;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

/* Load More Button Styling */
.load-more-button {
  font-weight: 600;
  margin-top: 16px;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.load-more-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

/* White Search Field Styling */
.white-search-field .v-input__control {
  background-color: white;
  border-radius: 8px;
}

.white-search-field .v-label {
  color: #555;
}

.white-search-field .v-input__icon--append .v-icon {
  color: #555;
}

/* Dialog Styling */
.v-dialog .v-card {
  border-radius: 16px;
}
</style>
